import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import { usePaywall } from '../components/Paywall';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { LockBadge } from '../components/UpgradePrompt';
import { 
  ArrowRight, 
  ArrowLeft,
  Bookmark,
  BookmarkSimple,
  CheckCircle,
  XCircle,
  Sparkle,
  SpinnerGap,
  Funnel,
  Warning,
  Crown,
  Book,
  CaretRight
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { showNewBadges } from '../components/BadgeToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PracticePage = () => {
  const [searchParams] = useSearchParams();
  const { sectionId, ruleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessCategory, pricing } = useFeatures();
  const paywall = usePaywall();
  const wrongStreakRef = useRef(0);
  const answeredCountRef = useRef(0);
  const correctCountRef = useRef(0);
  
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [aiExplanation, setAiExplanation] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [filterMode, setFilterMode] = useState('all');
  const [questionStats, setQuestionStats] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [expandedHCRule, setExpandedHCRule] = useState(null);
  const [ruleMode, setRuleMode] = useState(null); // {sectionId, ruleId, title}

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle rule-based practice mode
  useEffect(() => {
    if (sectionId && ruleId) {
      loadRuleQuestions(sectionId, ruleId);
    }
  }, [sectionId, ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRuleQuestions = async (sid, rid) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/highway-code/rules/${sid}/${rid}/questions`, { withCredentials: true });
      if (data.questions && data.questions.length > 0) {
        setQuestions(data.questions);
        setRuleMode({ sectionId: sid, ruleId: rid, title: `Rule ${rid}` });
        setQuizStarted(true);
        setCurrentIndex(0);
        setSelectedAnswer(null);
        setResult(null);
      }
    } catch (err) {
      console.error('Failed to load rule questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/questions/categories`, { withCredentials: true });
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionStats = async (category = '') => {
    try {
      const params = category ? `?category=${category}` : '';
      const { data } = await axios.get(`${API}/questions/stats${params}`, { withCredentials: true });
      setQuestionStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCategorySelect = async (category) => {
    // Check if category is locked
    if (category && !canAccessCategory(category)) {
      // Trigger paywall instead of navigating directly
      if (paywall) {
        const shown = await paywall.checkFeatureLock('locked_category');
        if (shown) return;
      }
      navigate('/subscription');
      return;
    }
    setSelectedCategory(category);
    await fetchQuestionStats(category);
    setShowFilterModal(true);
  };

  const startQuiz = async (category = '', filter = 'all') => {
    setLoading(true);
    setShowFilterModal(false);
    try {
      let params = `?limit=10&filter_mode=${filter}`;
      if (category) params += `&category=${category}`;
      
      const { data } = await axios.get(`${API}/questions/practice${params}`, { withCredentials: true });
      
      if (data.length === 0) {
        alert('No questions available with the selected filter. Try a different filter.');
        setLoading(false);
        return;
      }
      
      setQuestions(data);
      setQuizStarted(true);
      setCurrentIndex(0);
      setSelectedAnswer(null);
      setResult(null);
      setAiExplanation(null);
      setFilterMode(filter);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkBookmark = async (questionId) => {
    try {
      const { data } = await axios.get(`${API}/bookmarks/check/${questionId}`, { withCredentials: true });
      setIsBookmarked(data.bookmarked);
    } catch (error) {
      console.error('Failed to check bookmark:', error);
    }
  };

  useEffect(() => {
    if (questions[currentIndex]?.id) {
      checkBookmark(questions[currentIndex].id);
    }
  }, [currentIndex, questions]);

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null) return;
    setSubmitting(true);
    
    try {
      const { data } = await axios.post(`${API}/questions/answer`, {
        question_id: questions[currentIndex].id,
        selected_answer: selectedAnswer
      }, { withCredentials: true });
      
      setResult(data);
      if (data.new_badges?.length) {
        showNewBadges(data.new_badges);
      }

      // Track session metrics for paywall triggers
      answeredCountRef.current += 1;
      if (data.correct) {
        correctCountRef.current += 1;
        wrongStreakRef.current = 0;
      } else {
        wrongStreakRef.current += 1;
      }

      // Check paywall triggers after answer (only for free users)
      if (paywall) {
        // Struggle: 3+ wrong in a row
        if (wrongStreakRef.current >= 3) {
          await paywall.checkPaywall('quiz_struggle');
        }
        // Struggle: <50% accuracy after 5+ questions
        else if (answeredCountRef.current >= 5) {
          const accuracy = (correctCountRef.current / answeredCountRef.current) * 100;
          if (accuracy < 50) {
            await paywall.checkPaywall('quiz_struggle');
          }
        }
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleBookmark = async () => {
    try {
      const { data } = await axios.post(`${API}/bookmarks/toggle`, {
        question_id: questions[currentIndex].id
      }, { withCredentials: true });
      setIsBookmarked(data.bookmarked);
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  const handleGetAIExplanation = async () => {
    if (!user?.ai_enabled) return;
    setLoadingAI(true);
    
    try {
      const { data } = await axios.post(`${API}/ai/explanation`, {
        question_id: questions[currentIndex].id,
        selected_answer: selectedAnswer
      }, { withCredentials: true });
      setAiExplanation(data.explanation);
    } catch (error) {
      console.error('Failed to get AI explanation:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setResult(null);
      setAiExplanation(null);
      setExpandedHCRule(null);
    } else {
      // Quiz completed
      // Check success paywall: >80% accuracy
      if (paywall && answeredCountRef.current > 0) {
        const accuracy = (correctCountRef.current / answeredCountRef.current) * 100;
        if (accuracy > 80) {
          await paywall.checkPaywall('quiz_success');
        }
      }
      // Reset counters
      wrongStreakRef.current = 0;
      answeredCountRef.current = 0;
      correctCountRef.current = 0;
      // Refresh readiness
      if (paywall?.fetchReadiness) {
        paywall.fetchReadiness();
      }
      fetchCategories();
      setQuizStarted(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Filter Selection Modal
  if (showFilterModal) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="filter-modal">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setShowFilterModal(false)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Categories
          </button>

          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
              <Funnel weight="fill" className="w-4 h-4 inline mr-1" />
              Question Filter
            </p>
            <h1 className="font-heading font-black text-3xl tracking-tighter">
              {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'All Topics'}
            </h1>
          </div>

          {/* Stats Summary */}
          {questionStats && (
            <div className="bg-card border border-border rounded-sm p-4 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="font-heading font-bold text-2xl text-success">{questionStats.correct}</p>
                  <p className="text-xs text-muted-foreground">Mastered</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-destructive">{questionStats.failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-muted-foreground">{questionStats.unattempted}</p>
                  <p className="text-xs text-muted-foreground">Not Attempted</p>
                </div>
              </div>
            </div>
          )}

          {/* Filter Options */}
          <div className="space-y-3">
            <button
              onClick={() => startQuiz(selectedCategory, 'all')}
              className="w-full bg-card border border-border p-4 rounded-sm text-left hover:bg-muted transition-colors"
              data-testid="filter-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold">All Questions</p>
                  <p className="text-sm text-muted-foreground">Practice all questions in this category</p>
                </div>
                <span className="text-sm font-mono bg-muted px-2 py-1 rounded-sm">
                  {questionStats?.total || 0}
                </span>
              </div>
            </button>

            <button
              onClick={() => startQuiz(selectedCategory, 'failed')}
              disabled={!questionStats?.failed}
              className={cn(
                "w-full bg-card border p-4 rounded-sm text-left transition-colors",
                questionStats?.failed 
                  ? "border-destructive/50 hover:bg-destructive/10" 
                  : "border-border opacity-50 cursor-not-allowed"
              )}
              data-testid="filter-failed"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-destructive">Failed Only</p>
                  <p className="text-sm text-muted-foreground">Focus on questions you got wrong</p>
                </div>
                <span className="text-sm font-mono bg-destructive/20 text-destructive px-2 py-1 rounded-sm">
                  {questionStats?.failed || 0}
                </span>
              </div>
            </button>

            <button
              onClick={() => startQuiz(selectedCategory, 'failed_and_unattempted')}
              disabled={!questionStats?.failed_and_unattempted}
              className={cn(
                "w-full bg-card border p-4 rounded-sm text-left transition-colors",
                questionStats?.failed_and_unattempted 
                  ? "border-amber-500/50 hover:bg-amber-500/10" 
                  : "border-border opacity-50 cursor-not-allowed"
              )}
              data-testid="filter-failed-unattempted"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-amber-500">Failed + Not Attempted</p>
                  <p className="text-sm text-muted-foreground">Skip mastered questions</p>
                </div>
                <span className="text-sm font-mono bg-amber-500/20 text-amber-500 px-2 py-1 rounded-sm">
                  {questionStats?.failed_and_unattempted || 0}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Category Selection Screen
  if (!quizStarted) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="practice-page">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Practice Mode
            </p>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
              Choose a Topic
            </h1>
          </div>

          {/* All Topics Option */}
          <button
            onClick={() => handleCategorySelect('')}
            className="w-full bg-primary text-primary-foreground p-6 rounded-sm mb-6 text-left card-hover"
            data-testid="practice-all-topics"
          >
            <h3 className="font-heading font-bold text-xl mb-1">All Topics</h3>
            <p className="text-primary-foreground/80 text-sm">Practice random questions from all categories</p>
          </button>

          {/* Category Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const isLocked = cat.locked || !canAccessCategory(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.id)}
                  className={cn(
                    "bg-card border p-4 rounded-sm text-left card-hover relative",
                    isLocked ? "border-accent/30" : "border-border"
                  )}
                  data-testid={`category-${cat.id}`}
                >
                  {isLocked && <LockBadge />}
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading font-bold text-lg">{cat.name}</h3>
                    {cat.completion_percentage > 0 && !isLocked && (
                      <span className={cn(
                        "text-xs font-bold px-2 py-0.5 rounded-sm",
                        cat.completion_percentage >= 80 ? "bg-success/20 text-success" :
                        cat.completion_percentage >= 50 ? "bg-amber-500/20 text-amber-500" :
                        "bg-muted text-muted-foreground"
                      )}>
                        {cat.completion_percentage}%
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-mono">
                        <span className={isLocked ? "text-muted-foreground" : "text-success"}>{isLocked ? '--' : cat.correct_count}</span>
                        <span className="text-muted-foreground">/{cat.question_count}</span>
                      </span>
                    </div>
                    <Progress 
                      value={isLocked ? 0 : (cat.completion_percentage || 0)} 
                      className="h-2 rounded-none" 
                    />
                    <p className="text-xs text-muted-foreground">
                      {isLocked ? (
                        <span className="flex items-center gap-1 text-accent">
                          <Crown weight="fill" className="w-3 h-3" />
                          Upgrade to access this topic
                        </span>
                      ) : (
                        <>{cat.attempted_count} attempted &bull; {cat.question_count - cat.attempted_count} remaining</>
                      )}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progressPercent = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="quiz-page">
      {/* Header */}
      <div className="bg-card border-b border-border p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => {
              if (ruleMode) {
                navigate(`/highway-code?section=${ruleMode.sectionId}`);
              } else {
                fetchCategories(); setQuizStarted(false);
              }
            }}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="exit-quiz"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{ruleMode ? 'Back to Rule' : 'Exit'}</span>
          </button>
          <div className="flex items-center gap-4">
            {ruleMode && (
              <span className="text-xs px-2 py-1 rounded-sm font-bold bg-accent/20 text-accent flex items-center gap-1">
                <Book weight="fill" className="w-3 h-3" />
                {ruleMode.title}
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {questions.length}
            </span>
            {filterMode !== 'all' && (
              <span className={cn(
                "text-xs px-2 py-1 rounded-sm font-bold",
                filterMode === 'failed' ? "bg-destructive/20 text-destructive" : "bg-amber-500/20 text-amber-500"
              )}>
                {filterMode === 'failed' ? 'FAILED ONLY' : 'REVIEW MODE'}
              </span>
            )}
            <button
              onClick={handleToggleBookmark}
              className="p-2 hover:bg-muted rounded-sm transition-colors"
              data-testid="bookmark-button"
            >
              {isBookmarked ? (
                <Bookmark weight="fill" className="w-5 h-5 text-accent" />
              ) : (
                <BookmarkSimple className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-4">
          <Progress value={progressPercent} className="h-1 rounded-none" />
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="animate-slide-in">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">
            {currentQuestion.category}
          </p>
          <h2 className="font-heading font-bold text-xl md:text-2xl tracking-tight mb-8">
            {currentQuestion.question_text}
          </h2>

          {/* Question Image */}
          {currentQuestion.image_url && (
            <div className="mb-8 flex justify-center">
              <img
                src={currentQuestion.image_url}
                alt="Question illustration"
                className="max-w-xs w-full rounded-sm border border-border bg-muted p-2"
                data-testid="question-image"
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 mb-8">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = result && index === result.correct_answer;
              const isWrong = result && isSelected && !result.correct;

              return (
                <button
                  key={index}
                  onClick={() => !result && setSelectedAnswer(index)}
                  disabled={!!result}
                  className={cn(
                    "quiz-option w-full text-left",
                    isSelected && !result && "selected",
                    isCorrect && "correct",
                    isWrong && "incorrect"
                  )}
                  data-testid={`option-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 border",
                      isSelected && !result && "bg-primary border-primary text-primary-foreground",
                      isCorrect && "bg-success border-success text-white",
                      isWrong && "bg-destructive border-destructive text-white",
                      !isSelected && !isCorrect && !isWrong && "border-border"
                    )}>
                      {isCorrect ? (
                        <CheckCircle weight="fill" className="w-5 h-5" />
                      ) : isWrong ? (
                        <XCircle weight="fill" className="w-5 h-5" />
                      ) : (
                        <span className="font-bold text-sm">{String.fromCharCode(65 + index)}</span>
                      )}
                    </div>
                    <span className="pt-1">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Result & Explanation */}
          {result && (
            <div className="animate-fade-in mb-8">
              <div className={cn(
                "p-4 rounded-sm border mb-4",
                result.correct 
                  ? "bg-success/10 border-success/30" 
                  : "bg-destructive/10 border-destructive/30"
              )}>
                <div className="flex items-center gap-2 mb-2">
                  {result.correct ? (
                    <CheckCircle weight="fill" className="w-5 h-5 text-success" />
                  ) : (
                    <XCircle weight="fill" className="w-5 h-5 text-destructive" />
                  )}
                  <span className="font-bold">
                    {result.correct ? 'Correct!' : 'Incorrect'}
                  </span>
                  {result.streak && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      🔥 {result.streak.current_streak} day streak
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{result.explanation}</p>
              </div>

              {/* Related Highway Code Rules */}
              {result.related_rules && result.related_rules.length > 0 && (
                <div className="mb-4" data-testid="related-highway-code">
                  <div className="flex items-center gap-2 mb-2">
                    <Book weight="fill" className="w-4 h-4 text-accent" />
                    <span className="text-sm font-bold">Related Highway Code</span>
                  </div>
                  <div className="space-y-2">
                    {result.related_rules.map((rule, i) => (
                      <div key={i} className="border border-border rounded-sm bg-card overflow-hidden">
                        <button
                          onClick={() => setExpandedHCRule(expandedHCRule === rule.rule_id ? null : rule.rule_id)}
                          className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-muted/50 transition-colors"
                          data-testid={`hc-rule-${rule.rule_id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[10px] font-mono font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">
                              {rule.title?.match(/(H?\d+)/)?.[0] || rule.rule_id}
                            </span>
                            <span className="text-xs text-muted-foreground truncate">{rule.title}</span>
                          </div>
                          <CaretRight weight="bold" className={cn(
                            "w-3.5 h-3.5 text-muted-foreground transition-transform flex-shrink-0",
                            expandedHCRule === rule.rule_id && "rotate-90"
                          )} />
                        </button>
                        {expandedHCRule === rule.rule_id && (
                          <div className="px-3 pb-3 border-t border-border/50 pt-2 space-y-2">
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {rule.content?.length > 300 ? rule.content.slice(0, 300) + '...' : rule.content}
                            </p>
                            {rule.images && rule.images.length > 0 && (
                              <img
                                src={rule.images[0].url}
                                alt={rule.images[0].caption || 'Highway Code illustration'}
                                className="w-full max-h-40 object-contain rounded-sm border border-border bg-muted/30"
                                loading="lazy"
                              />
                            )}
                            <button
                              onClick={() => navigate(`/highway-code?section=${rule.section_id}`)}
                              className="text-[10px] text-accent hover:underline font-medium"
                              data-testid="view-full-rule"
                            >
                              View full section →
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Explanation */}
              {user?.ai_enabled && (
                <div className="space-y-3">
                  {!aiExplanation ? (
                    <Button
                      onClick={handleGetAIExplanation}
                      disabled={loadingAI}
                      variant="outline"
                      className="rounded-sm ai-glow"
                      data-testid="get-ai-explanation"
                    >
                      {loadingAI ? (
                        <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkle weight="fill" className="w-4 h-4 mr-2 text-accent" />
                      )}
                      Get AI Explanation
                    </Button>
                  ) : (
                    <div className="p-4 bg-accent/10 border border-accent/30 rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkle weight="fill" className="w-5 h-5 text-accent" />
                        <span className="font-bold text-sm">AI Tutor</span>
                      </div>
                      <p className="text-sm">{aiExplanation}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            {!result ? (
              <Button
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null || submitting}
                className="flex-1 rounded-sm"
                data-testid="submit-answer"
              >
                {submitting ? (
                  <SpinnerGap className="w-5 h-5 animate-spin" />
                ) : (
                  'Check Answer'
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="flex-1 rounded-sm"
                data-testid="next-question"
              >
                {currentIndex < questions.length - 1 ? (
                  <>
                    Next Question
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Finish Quiz
                    <CheckCircle className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
